import { useState } from "react";
import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";

import { ApiError } from "@/lib/api/client";
import { AuthProvider } from "@/lib/auth/AuthProvider";
import { FilePreviewProvider } from "@/features/library/FilePreview";

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              // Never retry a refusal: the answer will not change.
              if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
                return false;
              }
              return failureCount < 1;
            },
          },
          mutations: { retry: false },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FilePreviewProvider>{children}</FilePreviewProvider>
        {/* Dark, bottom left, with the way back on it.
            Clear of the tab bar on a phone. */}
        <Toaster
          position="bottom-left"
          closeButton
          mobileOffset={{ bottom: 76 }}
          toastOptions={{
            classNames: {
              toast:
                "!rounded-md !border-0 !bg-tip !text-tip-fg !font-sans !text-base !font-medium !shadow-pop",
              description: "!text-tip-fg/75",
              actionButton: "!bg-white/15 !text-white !rounded-xs !font-medium hover:!bg-white/25",
              closeButton: "!bg-tip !text-tip-fg !border-white/20",
            },
          }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}
